import {
  Ref,
  computed,
  inject,
  provide,
  ComputedRef,
  WritableComputedRef
} from "vue";
import {
  Button,
  Brand,
  AdTag,
  AdTagCampaign,
  Operator,
  Size,
  AdTagTeamFeatures
} from "@fresh8/types/src/index";
import autoInteractions from "./lib/autoInteractions";

export function createBrand(brand: Ref<Brand | undefined>) {
  const adTemplate = computed(() => brand.value?.adTemplate);
  const buttons = computed(() => brand.value?.buttons);
  const fonts = computed(() => brand.value?.fonts);
  const colors = computed(() => brand.value?.colors);
  const marketBranding = computed(() => brand.value?.adTemplate.market);
  const footerBranding = computed(() => brand.value?.adTemplate.footer);
  const headerBranding = computed(() => brand.value?.adTemplate.header);
  const operatorLogos = computed(() => brand.value?.operatorLogos);
  const offers = computed(() => brand.value?.offers);
  const backgroundImageSets = computed(() => brand.value?.backgroundImageSets);
  const displayEventOnly = computed(
    () => brand.value?.displayEventOnly ?? false
  );
  const eventOnly = computed(() => brand.value?.adTemplate.eventOnly);
  const prerollImageSets = computed(() => brand.value?.prerollImageSets);
  const usePreroll = computed(
    () => brand.value?.adTemplate.preroll?.usePreroll
  );
  const preroll = computed(() => brand.value?.adTemplate.preroll);
  const potentialWinnings = computed(
    () => brand.value?.adTemplate.potentialWinnings
  );
  const oddsDisplayTemplate = computed(
    () => marketBranding.value?.oddsButtonTemplate
  );
  const insights = computed(() =>
    brand.value?.adTemplate?.insights
      ? brand.value?.adTemplate?.insights
      : {
          enabledLeagues: [],
          enabledSports: []
        }
  );

  const getFontIdOrFallback = (id: string | undefined) => {
    if (Object.keys(fonts.value || {}).includes(id || "")) {
      return id;
    }

    return Object.keys(fonts.value || {})[0];
  };

  const operatorHasOffer = (operatorId: string) => {
    const operatorOffer = offers.value?.[operatorId];

    return (
      operatorOffer &&
      ((operatorOffer.body.length || 0) > 0 ||
        (operatorOffer.header.length || 0) > 0 ||
        (operatorOffer.buttonText.length || 0) > 0 ||
        (operatorOffer.terms.length || 0) > 0)
    );
  };

  const getColor = (colorKey: string | undefined) =>
    colors.value?.[colorKey || ""]?.color;

  const adTemplateBranding = computed(() => {
    return {
      backgroundColor: getColor(adTemplate.value?.backgroundColor),
      borderRadius: (() => {
        const borderRadius = adTemplate.value?.cardBorderRadius;
        if (borderRadius === 0) return 0;
        return borderRadius || 4;
      })(),
      cardBackgroundColor: getColor(adTemplate.value?.cardBackgroundColor),
      marketBorderColor:
        getColor(adTemplate.value?.marketBorderColor) || "#dcddf"
    };
  });

  const teamBranding = computed(() => ({
    ...adTemplate.value?.team,
    banner: {
      font: getFontIdOrFallback(adTemplate.value?.team?.banner?.font),
      backgroundColor:
        getColor(adTemplate.value?.team?.banner?.backgroundColor) ||
        getColor(Object.keys(colors.value || {})[0])
    },
    badge: {
      borderColor:
        getColor(adTemplate.value?.team?.badge?.borderColor) ||
        getColor(Object.keys(colors.value || {})[0])
    },
    versusIcon: {
      borderColor:
        getColor(adTemplate.value?.team?.versusIcon?.borderColor) || "#717171",
      backgroundColor:
        getColor(adTemplate.value?.team?.versusIcon?.backgroundColor) || "#fff",
      font: getFontIdOrFallback(adTemplate.value?.team?.versusIcon?.font)
    }
  }));

  const getColorStyle = (
    colorId: string | undefined,
    defaultColor?: string
  ) => {
    const colorPalette = colors.value?.[colorId || ""];

    if (!colorPalette) {
      return defaultColor || "#000";
    }

    return colorPalette.color;
  };

  const getFontStyle = (fontId: string | undefined, fallback?: boolean) => {
    let font = fonts.value?.[fontId || ""];

    if (fallback && !font) {
      font = fonts.value?.[Object.keys(fonts.value)[0]];
    }

    if (!font) {
      return {
        name: "Default font",
        color: "#000",
        fontFamily: "RegularRoboto"
      };
    }

    const { fontAsset, ...fontRest } = font;

    return {
      ...fontRest,
      color: getColorStyle(font.color),
      fontFamily: fontAsset?.fontFamily
    };
  };

  function getButtonStateColors(action) {
    return {
      primaryColor: getColorStyle(action.primaryColor),
      secondaryColor: getColorStyle(action.secondaryColor),
      backgroundColor: getColorStyle(action.backgroundColor),
      borderColor: getColorStyle(action.borderColor)
    };
  }

  const getButtonStyle = (buttonId: string | undefined, fallback?: boolean) => {
    let button: Button | undefined = buttons.value?.[buttonId || ""];
    const defaultButton = buttons.value && Object.keys(buttons.value)[0];

    if (fallback && !button && defaultButton) {
      button = buttons.value[defaultButton];
    } else if (fallback && !button && !defaultButton) {
      button = {
        name: "",
        type: "odds",
        primaryFont: "",
        secondaryFont: "",
        autoInteractions: true,
        iconColor: "",
        actions: {},
        animations: {
          entry: "none",
          repeat: "none"
        }
      };
    }

    const buttonBackgroundColor = getColorStyle(button?.backgroundColor || "");
    const buttonPrimaryFontColor =
      getFontStyle(button?.primaryFont || "", true)?.color || "#000";
    const buttonSecondaryFontColor =
      getFontStyle(button?.primaryFont || "", true)?.color || "#000";

    const autoInteractionsStyles = autoInteractions({
      backgroundColor: buttonBackgroundColor || "#fff",
      primaryColor: buttonPrimaryFontColor,
      secondaryColor: buttonSecondaryFontColor
    });

    return {
      ...button,
      iconColor: getColorStyle(button?.iconColor || "#000"),
      backgroundColor: buttonBackgroundColor,
      borderColor: getColorStyle(button?.borderColor || ""),
      actions: {
        hover: button?.actions.hover
          ? getButtonStateColors(button.actions.hover)
          : autoInteractionsStyles.hover,
        active: button?.actions.active
          ? getButtonStateColors(button.actions.active)
          : autoInteractionsStyles.active,
        selected: button?.actions.selected
          ? getButtonStateColors(button.actions.selected)
          : autoInteractionsStyles.selected
      }
    };
  };

  // To be able to use Vue v-bind in css, we need to append the units
  // to the size properties, as we can't interpolate that into the style sheet
  // like with JSS.
  // https://vuejs.org/api/sfc-css-features.html#v-bind-in-css
  function getFormattedButtonStyle(buttonId: string, fallback?: boolean) {
    const buttonStyle = getButtonStyle(buttonId, fallback);
    return {
      ...buttonStyle,
      borderRadius: buttonStyle.borderRadius + "px",
      borderWidth: buttonStyle.borderWidth + "px"
    };
  }

  const enableBackgroundClick = computed(
    () => brand.value?.adTemplate.enableBackgroundClick || false
  );

  return {
    adTemplate,
    brand,
    fonts,
    buttons,
    offers,
    insights,
    teamBranding,
    adTemplateBranding,
    headerBranding,
    marketBranding,
    footerBranding,
    operatorLogos,
    backgroundImageSets,
    preroll,
    usePreroll,
    prerollImageSets,
    potentialWinnings,
    getColorStyle,
    getFontStyle,
    getButtonStyle,
    getFormattedButtonStyle,
    operatorHasOffer,
    oddsDisplayTemplate,
    enableBackgroundClick,
    displayEventOnly,
    eventOnly
  };
}

export type BrandContext = ReturnType<typeof createBrand>;

export type FontStyle = ReturnType<BrandContext["getFontStyle"]>;
export type ButtonStyle = ReturnType<BrandContext["getButtonStyle"]>;

export const brandSymbol = Symbol("brandSymbol");

export function provideBrand(brand: Ref<Brand | undefined>) {
  const _brand = createBrand(brand);

  provide(brandSymbol, _brand);

  return _brand;
}

export function injectBrand(): BrandContext | undefined {
  return inject<BrandContext>(brandSymbol);
}

export function createAdTag(
  adTag: Ref<AdTag | undefined>,
  selectedOperator:
    | WritableComputedRef<Operator | undefined>
    | Ref<Operator | undefined>,
  selectedCampaign: ComputedRef<AdTagCampaign | undefined>
): {
  responsive: ComputedRef<boolean>;
  adTagSize: ComputedRef<Size | undefined>;
  adTag: Ref<AdTag | undefined>;
  operator: ComputedRef<Operator | undefined>;
  operators: ComputedRef<Operator[] | undefined>;
  selectedOperator:
    | WritableComputedRef<Operator | undefined>
    | Ref<Operator | undefined>;
  selectedCampaign: ComputedRef<AdTagCampaign | undefined>;
  teamFeatureFlags: ComputedRef<AdTagTeamFeatures | undefined>;
} {
  const responsive = computed(() => {
    return !!adTag.value?.size?.responsive;
  });

  const adTagSize = computed(() => adTag.value?.size);
  const operators = computed(() => adTag.value?.operators);

  const operator = computed(() => {
    if (adTag.value?.operator) {
      return adTag.value?.operator;
    }

    return selectedOperator.value;
  });

  const teamFeatureFlags = computed(() => {
    if (typeof adTag.value?.team === "object") {
      return adTag.value?.team.features;
    }
  });

  return {
    responsive,
    adTagSize,
    adTag,
    operator,
    operators,
    selectedOperator,
    selectedCampaign,
    teamFeatureFlags
  };
}

export type AdTagContext = ReturnType<typeof createAdTag>;
export const adTagSymbol = Symbol("AdTagSymbol");

export function provideAdTag(
  adTag: Ref<AdTag | undefined>,
  selectedOperator:
    | WritableComputedRef<Operator | undefined>
    | Ref<Operator | undefined>,
  selectedCampaign: ComputedRef<AdTagCampaign | undefined>
): AdTagContext {
  const _adTag = createAdTag(adTag, selectedOperator, selectedCampaign);
  provide(adTagSymbol, _adTag);
  return _adTag;
}

export function injectAdTag(): AdTagContext | undefined {
  return inject<AdTagContext>(adTagSymbol);
}
